import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Folder, FileText, File, Lock, ChevronRight, ChevronDown, 
  Download, Eye, ArrowLeft, Shield, FolderOpen, Image, 
  FileSpreadsheet, FileType, Video, Music
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// File type icons
const getFileIcon = (type) => {
  switch(type?.toLowerCase()) {
    case 'pdf': return <FileText className="text-red-400" size={20} />;
    case 'xlsx': case 'xls': return <FileSpreadsheet className="text-green-400" size={20} />;
    case 'docx': case 'doc': return <FileType className="text-blue-400" size={20} />;
    case 'jpg': case 'jpeg': case 'png': case 'psd': return <Image className="text-purple-400" size={20} />;
    case 'mp4': case 'mov': return <Video className="text-pink-400" size={20} />;
    case 'mp3': case 'wav': return <Music className="text-orange-400" size={20} />;
    default: return <File className="text-gray-400" size={20} />;
  }
};

const DataRoom = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [structure, setStructure] = useState(null);
  const [files, setFiles] = useState([]);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [accessForm, setAccessForm] = useState({ name: '', email: '', company: '' });
  const [showAccessForm, setShowAccessForm] = useState(false);

  // Check if already authenticated
  useEffect(() => {
    const auth = sessionStorage.getItem('dataroom_auth');
    if (auth === 'true') {
      setIsAuthenticated(true);
      fetchData();
    }
  }, []);

  const fetchData = async () => {
    try {
      const [structureRes, filesRes] = await Promise.all([
        axios.get(`${API}/dataroom/structure`),
        axios.get(`${API}/dataroom/files`)
      ]);
      setStructure(structureRes.data);
      setFiles(filesRes.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/dataroom/verify`, { password });
      setShowAccessForm(true);
    } catch (err) {
      setError('Invalid password. Please contact us for access.');
    }
    setLoading(false);
  };

  const handleAccessSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/dataroom/register-access`, accessForm);
      sessionStorage.setItem('dataroom_auth', 'true');
      setIsAuthenticated(true);
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => ({
      ...prev,
      [folderId]: !prev[folderId]
    }));
  };

  const getFilesForFolder = (folderPath) => {
    return files.filter(f => f.folder_path === folderPath);
  };

  // Login/Access Request Page
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#1A1A1A] pt-24">
        <div className="max-w-xl mx-auto px-4 py-16">
          <div className="text-center mb-12">
            <Shield className="mx-auto text-[#FEDD00] mb-4" size={64} />
            <h1 className="text-4xl font-bold text-[#FEDD00] mb-4">INVESTOR DATA ROOM</h1>
            <p className="text-gray-400">
              Secure access to TALLAWAH production documents, financials, and materials.
            </p>
          </div>

          <div className="bg-[#2A2A2A] p-8 rounded-lg border border-[#FEDD00]/30">
            {!showAccessForm ? (
              <>
                <div className="flex items-center gap-3 mb-6 pb-6 border-b border-[#FEDD00]/20">
                  <Lock className="text-[#FEDD00]" size={24} />
                  <h2 className="text-xl font-bold text-white">Password Required</h2>
                </div>
                {error && (
                  <div className="bg-red-500/20 border border-red-500 text-red-400 p-3 rounded mb-4">
                    {error}
                  </div>
                )}
                <form onSubmit={handleLogin} className="space-y-4">
                  <input
                    type="password"
                    placeholder="Enter data room password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full p-4 bg-[#1A1A1A] border border-[#FEDD00]/30 text-white rounded focus:border-[#FEDD00] outline-none"
                    required
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#FEDD00] text-black font-bold py-4 rounded hover:bg-yellow-400 transition-colors"
                  >
                    {loading ? 'Verifying...' : 'Access Data Room'}
                  </button>
                </form>
                <p className="text-center text-gray-500 mt-6 text-sm">
                  Don't have access? <Link to="/contact" className="text-[#FEDD00] hover:underline">Request access</Link>
                </p>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-6 pb-6 border-b border-[#FEDD00]/20">
                  <Eye className="text-[#FEDD00]" size={24} />
                  <h2 className="text-xl font-bold text-white">Register Your Access</h2>
                </div>
                <p className="text-gray-400 mb-6">
                  Please provide your information to access the data room. This helps us track who is viewing our materials.
                </p>
                <form onSubmit={handleAccessSubmit} className="space-y-4">
                  <input
                    type="text"
                    placeholder="Your Name *"
                    value={accessForm.name}
                    onChange={(e) => setAccessForm({...accessForm, name: e.target.value})}
                    className="w-full p-4 bg-[#1A1A1A] border border-[#FEDD00]/30 text-white rounded focus:border-[#FEDD00] outline-none"
                    required
                  />
                  <input
                    type="email"
                    placeholder="Email Address *"
                    value={accessForm.email}
                    onChange={(e) => setAccessForm({...accessForm, email: e.target.value})}
                    className="w-full p-4 bg-[#1A1A1A] border border-[#FEDD00]/30 text-white rounded focus:border-[#FEDD00] outline-none"
                    required
                  />
                  <input
                    type="text"
                    placeholder="Company/Organization (Optional)"
                    value={accessForm.company}
                    onChange={(e) => setAccessForm({...accessForm, company: e.target.value})}
                    className="w-full p-4 bg-[#1A1A1A] border border-[#FEDD00]/30 text-white rounded focus:border-[#FEDD00] outline-none"
                  />
                  <button
                    type="submit"
                    className="w-full bg-[#FEDD00] text-black font-bold py-4 rounded hover:bg-yellow-400 transition-colors"
                  >
                    Enter Data Room
                  </button>
                </form>
              </>
            )}
          </div>

          <div className="mt-8 text-center">
            <Link to="/" className="text-gray-400 hover:text-[#FEDD00] inline-flex items-center gap-2">
              <ArrowLeft size={20} /> Back to Website
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Data Room Content
  return (
    <div className="min-h-screen bg-[#1A1A1A] pt-24">
      {/* Header */}
      <div className="bg-gradient-to-b from-[#006847]/30 to-[#1A1A1A] py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-[#FEDD00] mb-2">DATA ROOM</h1>
              <p className="text-gray-400">TALLAWAH JAMAICANS IN NYC - Confidential Materials</p>
            </div>
            <button
              onClick={() => {
                sessionStorage.removeItem('dataroom_auth');
                setIsAuthenticated(false);
              }}
              className="text-gray-400 hover:text-white flex items-center gap-2"
            >
              <Lock size={18} /> Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Folder Structure */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="bg-[#2A2A2A] rounded-lg border border-[#FEDD00]/20 overflow-hidden">
          {/* Breadcrumb */}
          <div className="bg-[#1A1A1A] p-4 border-b border-[#FEDD00]/20 flex items-center gap-2">
            <Folder className="text-[#FEDD00]" size={20} />
            <span className="text-[#FEDD00] font-semibold">TALLAWAH_DATA_ROOM</span>
          </div>

          {/* Folders */}
          <div className="divide-y divide-[#FEDD00]/10">
            {structure?.folders?.map((folder) => (
              <div key={folder.id} className="group">
                {/* Main Folder */}
                <button
                  onClick={() => toggleFolder(folder.id)}
                  className="w-full flex items-center gap-3 p-4 hover:bg-[#1A1A1A] transition-colors text-left"
                >
                  {expandedFolders[folder.id] ? (
                    <ChevronDown className="text-[#FEDD00]" size={20} />
                  ) : (
                    <ChevronRight className="text-gray-500" size={20} />
                  )}
                  {expandedFolders[folder.id] ? (
                    <FolderOpen className="text-[#FEDD00]" size={24} />
                  ) : (
                    <Folder className="text-[#FEDD00]" size={24} />
                  )}
                  <span className="font-semibold text-white">{folder.id}_{folder.name}</span>
                  {folder.subfolders?.length > 0 && (
                    <span className="text-gray-500 text-sm ml-auto">
                      {folder.subfolders.length} subfolder{folder.subfolders.length > 1 ? 's' : ''}
                    </span>
                  )}
                </button>

                {/* Expanded Content */}
                {expandedFolders[folder.id] && (
                  <div className="bg-[#1A1A1A]/50 border-t border-[#FEDD00]/10">
                    {/* Files in main folder */}
                    {getFilesForFolder(folder.path).map((file) => (
                      <div key={file.id} className="flex items-center gap-3 px-12 py-3 hover:bg-[#1A1A1A] border-b border-[#FEDD00]/5">
                        {getFileIcon(file.file_type)}
                        <span className="text-gray-300 flex-1">{file.file_name}</span>
                        {file.file_url ? (
                          <a
                            href={file.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#FEDD00] hover:text-yellow-400 flex items-center gap-1"
                          >
                            <Download size={16} /> Download
                          </a>
                        ) : (
                          <span className="text-gray-600 text-sm">Coming soon</span>
                        )}
                      </div>
                    ))}

                    {/* Subfolders */}
                    {folder.subfolders?.map((subfolder, idx) => (
                      <div key={idx}>
                        <button
                          onClick={() => toggleFolder(`${folder.id}-${idx}`)}
                          className="w-full flex items-center gap-3 px-8 py-3 hover:bg-[#1A1A1A] transition-colors text-left border-b border-[#FEDD00]/5"
                        >
                          {expandedFolders[`${folder.id}-${idx}`] ? (
                            <ChevronDown className="text-[#FEDD00]" size={16} />
                          ) : (
                            <ChevronRight className="text-gray-500" size={16} />
                          )}
                          {expandedFolders[`${folder.id}-${idx}`] ? (
                            <FolderOpen className="text-[#FEDD00]/70" size={20} />
                          ) : (
                            <Folder className="text-[#FEDD00]/70" size={20} />
                          )}
                          <span className="text-gray-300">{subfolder.name}</span>
                        </button>

                        {/* Subfolder files */}
                        {expandedFolders[`${folder.id}-${idx}`] && (
                          <div className="bg-[#0A0A0A]/50">
                            {getFilesForFolder(subfolder.path).length > 0 ? (
                              getFilesForFolder(subfolder.path).map((file) => (
                                <div key={file.id} className="flex items-center gap-3 px-16 py-3 hover:bg-[#1A1A1A] border-b border-[#FEDD00]/5">
                                  {getFileIcon(file.file_type)}
                                  <span className="text-gray-400 flex-1">{file.file_name}</span>
                                  {file.file_url ? (
                                    <a
                                      href={file.file_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-[#FEDD00] hover:text-yellow-400 flex items-center gap-1"
                                    >
                                      <Download size={16} /> Download
                                    </a>
                                  ) : (
                                    <span className="text-gray-600 text-sm">Coming soon</span>
                                  )}
                                </div>
                              ))
                            ) : (
                              <div className="px-16 py-3 text-gray-600 text-sm italic">
                                No files uploaded yet
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Empty state for main folder */}
                    {getFilesForFolder(folder.path).length === 0 && folder.subfolders?.length === 0 && (
                      <div className="px-12 py-4 text-gray-600 text-sm italic">
                        No files uploaded yet
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-8 p-6 bg-[#2A2A2A] rounded-lg border border-[#FEDD00]/20">
          <p className="text-gray-400 text-sm text-center">
            <strong className="text-[#FEDD00]">CONFIDENTIAL:</strong> The materials in this data room are confidential and intended solely for authorized recipients. 
            Unauthorized distribution, copying, or disclosure is strictly prohibited.
          </p>
        </div>

        <div className="mt-8 text-center">
          <Link to="/" className="text-gray-400 hover:text-[#FEDD00] inline-flex items-center gap-2">
            <ArrowLeft size={20} /> Back to Website
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DataRoom;
